import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { z } from "zod";
import {
  ArrowRight,
  Check,
  Users,
  ListChecks,
  Wallet,
  Facebook,
  Instagram,
  Globe,
  Link2,
  Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import heroWedding from "@/assets/hero-wedding.jpg";
import {
  type Locale,
  type Translations,
  getTranslations,
  SUPPORTED_LOCALES,
  detectLocale,
} from "@/i18n/translations";

const emailSchema = z.string().trim().email().max(255);
type WaitlistFormId = "hero" | "final";
const Logo = ({ size = 34 }: { size?: number }) => (
  <span className="inline-flex items-center gap-2 font-serif text-[22px] font-semibold text-foreground">
    <img src="/nellove-mark.png" alt="" width={size} height={size} className="object-contain" aria-hidden="true" />
    <span>Nellove</span>
  </span>
);

type WaitlistFormProps = {
  idPrefix: string;
  formId: WaitlistFormId;
  t: Translations;
  shareUrl: string;
  onSubmit: (
    values: { email: string; consent: boolean },
    formId: WaitlistFormId,
  ) => Promise<boolean>;
};

const WaitlistForm = ({ idPrefix, formId, t, shareUrl, onSubmit }: WaitlistFormProps) => {
  const formRef = useRef<HTMLFormElement>(null);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState(false);

  const labelClass = "text-stone-700";

  if (done) {
    const shareText = `${t.form.shareText} ${shareUrl}`;
    return (
      <div
        className="border-l-4 border-primary bg-card p-6"
        role="status"
        aria-live="polite"
      >
        <div className="flex items-start gap-3">
          <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-sage-100 text-sage-600">
            <Check className="h-4 w-4" aria-hidden="true" />
          </span>
          <div>
            <p className="font-serif text-xl text-foreground">{t.form.successTitle}</p>
            <p className="mt-1 text-[15px] leading-relaxed text-stone-600">{t.form.successBody}</p>
          </div>
        </div>
        <p className="mt-5 text-sm text-stone-600">{t.form.shareIntro}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            variant="outline"
            type="button"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(shareUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              } catch {
                setCopied(false);
              }
            }}
            className="h-10 gap-2 rounded-md"
          >
            <Link2 className="h-4 w-4" aria-hidden="true" />
            {copied ? t.form.shareCopied : t.form.shareCopy}
          </Button>
          <a
            href={`mailto:?subject=${encodeURIComponent(t.form.shareSubject)}&body=${encodeURIComponent(shareText)}`}
            className="inline-flex h-10 items-center gap-2 rounded-xl border border-stone-200 bg-background px-3.5 text-sm text-stone-700 transition-colors hover:border-rust-400 hover:text-rust-600"
          >
            <Mail className="h-4 w-4" aria-hidden="true" />
            {t.form.shareEmail}
          </a>
        </div>
      </div>
    );
  }

  return (
    <form
      ref={formRef}
      noValidate
      aria-label={t.form.formLabel}
      onSubmit={async (e) => {
        e.preventDefault();
        const parsedEmail = emailSchema.safeParse(email);
        if (!parsedEmail.success) {
          setError(t.form.invalidEmail);
          return;
        }
        setError(null);
        setLoading(true);
        const ok = await onSubmit(
          {
            email: parsedEmail.data,
            consent,
          },
          formId,
        );
        setLoading(false);
        if (ok) {
          setDone(true);
          setEmail("");
        } else {
          setError(t.form.genericError);
        }
      }}
       className="w-full"
    >
      <div>
          <label htmlFor={`${idPrefix}-email`} className={`mb-2 block text-sm font-semibold ${labelClass}`}>
            {t.form.emailLabel}
          </label>
          <Input
            id={`${idPrefix}-email`}
            name="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.form.emailPlaceholder}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${idPrefix}-error` : undefined}
              className="h-14 rounded-md border-stone-200 bg-card px-4 text-base text-foreground shadow-none placeholder:text-stone-600 focus-visible:border-rust-500 focus-visible:ring-primary"
          />
      </div>

      <div className="mt-4 flex items-start gap-2.5">
        <Checkbox
          id={`${idPrefix}-consent`}
          checked={consent}
          onCheckedChange={(checked) => setConsent(checked === true)}
          required
          aria-required="true"
          className="mt-0.5"
        />
        <label
          htmlFor={`${idPrefix}-consent`}
          className={`cursor-pointer text-[13px] leading-relaxed ${labelClass}`}
        >
          {t.form.consent}
        </label>
      </div>

      {error && (
        <p id={`${idPrefix}-error`} role="alert" className="mt-3 text-sm font-medium text-destructive">
          {error}
        </p>
      )}

      <Button
        type="submit"
        disabled={loading || !consent}
        aria-busy={loading}
          className="mt-5 h-14 w-full gap-2 rounded-md bg-primary px-6 py-4 text-base font-bold text-primary-foreground shadow-lift transition-colors duration-200 hover:bg-rust-600"
      >
        {loading ? t.form.ctaLoading : t.form.cta}
        {!loading && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
      </Button>

       <p className="mt-3 text-center text-[13px] leading-relaxed text-stone-600">
        {t.form.privacy}
      </p>
    </form>
  );
};

const Index = () => {
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = (SUPPORTED_LOCALES.includes(locale as Locale) ? locale : detectLocale()) as Locale;
  const t: Translations = getTranslations(currentLocale);
  const navigate = useNavigate();
  const [showSticky, setShowSticky] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const finalRef = useRef<HTMLDivElement>(null);

  const switchLocale = (newLocale: Locale) => navigate(`/${newLocale}`, { replace: true });
  const otherLocale: Locale = currentLocale === "en" ? "fr" : "en";
  const baseUrl = "https://nellove.com";
  const canonicalUrl = `${baseUrl}/${currentLocale}`;
  const socialImageUrl = `${baseUrl}/images/nellove-cover.png`;
  const ogLocale = currentLocale === "fr" ? "fr_FR" : "en_US";
  const alternateOgLocale = otherLocale === "fr" ? "fr_FR" : "en_US";

  useEffect(() => {
    document.documentElement.lang = currentLocale;
    document.querySelectorAll('[data-static-meta="nellove"]').forEach((node) => node.remove());
  }, [currentLocale]);

  useEffect(() => {
    const hero = heroRef.current;
    const final = finalRef.current;
    if (!hero || !final) return;
    const state = { heroVisible: true, finalVisible: false };
    const update = () => setShowSticky(!state.heroVisible && !state.finalVisible);
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === hero) state.heroVisible = entry.isIntersecting;
          if (entry.target === final) state.finalVisible = entry.isIntersecting;
        });
        update();
      },
      { threshold: 0.05 },
    );
    io.observe(hero);
    io.observe(final);
    return () => io.disconnect();
  }, []);

  const handleSubmit = async (
    values: { email: string; consent: boolean },
    formId: WaitlistFormId,
  ) => {
    try {
      const { error } = await supabase.from("waitlist").insert({
        email: values.email,
        locale: currentLocale,
        first_name: null,
        segment: null,
        marketing_consent: values.consent,
      });
      if (error && error.code !== "23505") {
        return false;
      }
      toast({ title: t.form.successTitle, description: t.form.successBody });
      return true;
    } catch {
      return false;
    }
  };

  const solutionIcons = [Users, ListChecks, Wallet];

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <Helmet>
        <html lang={currentLocale} />
        <title>{t.meta.title}</title>
        <meta name="description" content={t.meta.description} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang="en" href={`${baseUrl}/en`} />
        <link rel="alternate" hrefLang="fr" href={`${baseUrl}/fr`} />
        <link rel="alternate" hrefLang="x-default" href={`${baseUrl}/en`} />
        <meta property="og:title" content={t.meta.title} />
        <meta property="og:description" content={t.meta.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={socialImageUrl} />
        <meta property="og:image:alt" content={t.a11y.logoAlt} />
        <meta property="og:locale" content={ogLocale} />
        <meta property="og:locale:alternate" content={alternateOgLocale} />
        <meta property="og:site_name" content="Nellove" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t.meta.title} />
        <meta name="twitter:description" content={t.meta.description} />
        <meta name="twitter:image" content={socialImageUrl} />
        <meta name="twitter:image:alt" content={t.a11y.logoAlt} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Organization",
                "@id": `${baseUrl}/#organization`,
                name: "Nellove",
                url: baseUrl,
                logo: `${baseUrl}/nellove-logo.png`,
                description: t.meta.description,
                sameAs: [
                  "https://www.facebook.com/profile.php?id=61583920627069",
                  "https://www.instagram.com/nellove.app/",
                ],
              },
              {
                "@type": "WebSite",
                "@id": `${baseUrl}/#website`,
                url: baseUrl,
                name: "Nellove",
                description: t.meta.description,
                inLanguage: currentLocale === "fr" ? "fr-FR" : "en-US",
                publisher: { "@id": `${baseUrl}/#organization` },
              },
              {
                "@type": "SoftwareApplication",
                "@id": `${baseUrl}/#app`,
                name: "Nellove",
                applicationCategory: "LifestyleApplication",
                operatingSystem: "Web",
                url: baseUrl,
                inLanguage: ["en", "fr"],
                description:
                   "Nellove is a wedding planning workspace for couples, wedding planners and agencies: guests and RSVP, a date-aware checklist, budget and day-of timeline, in English and French. Early access is by invitation.",
                publisher: { "@id": `${baseUrl}/#organization` },
              },
            ],
          })}
        </script>
      </Helmet>

      <a href="#main-content" className="skip-link">
        {t.a11y.skipToContent}
      </a>

       <header className="sticky top-0 z-40 border-b border-stone-200 bg-background/95 backdrop-blur-md">
        <div className="mx-auto flex h-[68px] max-w-6xl items-center justify-between px-5 sm:px-6">
          <a href={`/${currentLocale}`} aria-label="Nellove">
            <Logo size={30} />
          </a>
           <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
            <a
              href={currentLocale === "fr" ? "/fr/application-mariage" : "/en/wedding-planning-app"}
              className="text-[15px] text-stone-700 transition-colors duration-200 hover:text-rust-600 hover:underline"
            >
              {t.nav.product}
            </a>
            <a
              href={currentLocale === "fr" ? "/fr/pour-organisateurs-de-mariage" : "/en/for-wedding-planners"}
              className="text-[15px] text-stone-700 transition-colors duration-200 hover:text-rust-600 hover:underline"
            >
              {t.nav.planners}
            </a>
            <a
              href={currentLocale === "fr" ? "/fr/checklist-mariage" : "/en/wedding-planning-checklist"}
              className="text-[15px] text-stone-700 transition-colors duration-200 hover:text-rust-600 hover:underline"
            >
              {t.nav.checklist}
            </a>
            <a
              href={`/${currentLocale}/guides`}
              className="text-[15px] text-stone-700 transition-colors duration-200 hover:text-rust-600 hover:underline"
            >
              {t.nav.guides}
            </a>
          </nav>

           <div className="flex items-center gap-2">
             <Button
               type="button"
               variant="ghost"
              onClick={() => switchLocale(otherLocale)}
               className="h-10 gap-1.5 rounded-lg px-2.5 text-sm font-normal text-stone-700 hover:bg-rust-50 hover:text-rust-600"
              aria-label={`${t.langSwitch.label}: ${t.langSwitch[otherLocale]}`}
            >
              <Globe className="h-4 w-4" aria-hidden="true" />
              {t.langSwitch[otherLocale]}
             </Button>
             <Button asChild className="hidden h-11 rounded-md px-5 text-sm font-bold shadow-lift sm:inline-flex">
               <a href="#join">
              {t.nav.join}
               </a>
             </Button>
          </div>
        </div>
      </header>

      <main id="main-content" className="flex-1">
        {/* Hero */}
           <section ref={heroRef} className="overflow-hidden border-b border-stone-200 bg-background" aria-labelledby="hero-heading">
             <div className="mx-auto grid max-w-6xl items-center gap-8 px-5 py-10 sm:px-6 md:py-16 lg:min-h-[min(780px,calc(100svh-68px))] lg:grid-cols-[minmax(0,1.18fr)_minmax(390px,.82fr)] lg:gap-16 lg:py-20">
              <div className="animate-fade-in min-w-0">
                 <p className="inline-flex items-center border-b border-rust-500 pb-2 text-xs font-bold uppercase text-rust-600">
                {t.hero.eyebrow}
              </p>
              <h1
                id="hero-heading"
                   className="mt-7 max-w-[15ch] text-[42px] leading-[1.06] text-foreground sm:text-[58px] lg:text-[66px]"
              >
                {t.hero.heading}
              </h1>
                 <p className="mt-6 max-w-[53ch] text-[17px] leading-relaxed text-stone-700 sm:text-[19px]">
                {t.hero.subtitle}
              </p>
                <div className="mt-8 hidden max-w-lg overflow-hidden border border-stone-200 sm:block">
                  <img src={heroWedding} alt={t.hero.imageAlt} width="1152" height="768" className="h-40 w-full object-cover object-center" loading="eager" />
                </div>
                <a href="#problem" className="mt-6 hidden items-center gap-2 text-sm font-semibold text-stone-700 hover:text-rust-600 hover:underline sm:inline-flex">
                  {t.hero.learnMore}<ArrowRight className="h-4 w-4" aria-hidden="true" />
                </a>
            </div>
              <div id="join" className="animate-fade-in scroll-mt-24 bg-secondary p-3 sm:p-6 lg:translate-y-8">
                <div className="border border-stone-200 bg-card p-5 shadow-soft sm:p-8">
                  <p className="text-xs font-bold uppercase text-rust-600">{t.hero.eyebrow}</p>
                  <h2 className="mt-4 text-[30px] leading-tight sm:text-[34px]">{t.form.requestTitle}</h2>
                  <p className="mt-3 mb-7 text-[15px] leading-relaxed text-stone-600">{t.form.requestBody}</p>
                  <WaitlistForm idPrefix="hero" formId="hero" t={t} shareUrl={canonicalUrl} onSubmit={handleSubmit} />
                </div>
              </div>
          </div>
        </section>

        {/* Problem */}
         <section id="problem" className="scroll-mt-20 border-b border-stone-200 bg-secondary px-5 py-24 sm:px-6 md:py-32">
           <div className="mx-auto grid max-w-6xl gap-10 md:grid-cols-[minmax(0,1.15fr)_minmax(280px,.85fr)] md:items-end">
             <div>
             <p className="text-[12px] font-medium uppercase tracking-[0.08em] text-stone-600">{t.problem.eyebrow}</p>
             <h2 className="mt-4 max-w-[18ch] text-[38px] leading-[1.04] sm:text-[52px]">{t.problem.heading}</h2>
             <p className="mt-5 max-w-[50ch] text-[17px] leading-relaxed text-stone-600">{t.problem.body}</p>
             </div>
             <ul className="space-y-0 border-t border-stone-200">
              {t.problem.points.map((point) => (
                 <li key={point} className="border-b border-stone-200 py-5 text-[16px] leading-relaxed text-stone-700">
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Solution */}
         <section className="px-5 py-24 sm:px-6 md:py-32">
           <div className="mx-auto max-w-6xl">
              <p className="text-[12px] font-medium uppercase tracking-[0.08em] text-stone-600">{t.solution.eyebrow}</p>
             <h2 className="mt-4 max-w-[20ch] text-[38px] leading-[1.04] sm:text-[52px]">
              {t.solution.heading}
            </h2>
            <p className="mt-4 max-w-[52ch] text-[17px] leading-relaxed text-stone-600">{t.solution.subtitle}</p>
             <div className="mt-14 grid gap-px overflow-hidden rounded-xl border border-stone-200 bg-stone-200 md:grid-cols-3">
              {t.solution.items.map((item, i) => {
                const Icon = solutionIcons[i] ?? Users;
                return (
                   <article key={item.title} className="min-h-64 bg-card p-7 transition-colors duration-200 hover:bg-rust-50 sm:p-8">
                     <span className="grid h-11 w-11 place-items-center rounded-lg border border-rust-400 bg-card text-rust-600">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <h3 className="mt-4 font-serif text-[21px]">{item.title}</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-stone-600">{item.body}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* How it works */}
         <section id="how" className="scroll-mt-20 bg-background px-5 py-24 sm:px-6 md:py-32">
           <div className="mx-auto max-w-6xl">
              <p className="text-[12px] font-medium uppercase tracking-[0.08em] text-stone-600">{t.how.eyebrow}</p>
             <h2 className="mt-4 text-[38px] leading-[1.04] sm:text-[52px]">{t.how.heading}</h2>
             <ol className="mt-14 grid gap-0 border-y border-stone-200 md:grid-cols-3 md:divide-x md:divide-stone-200">
              {t.how.steps.map((step, i) => (
                  <li key={step.title} className="bg-background px-0 py-8 md:px-8 first:pl-0 last:pr-0">
                  <span className="grid h-9 w-9 place-items-center rounded-full border border-rust-500 font-serif text-[15px] text-rust-600">
                    {i + 1}
                  </span>
                  <h3 className="mt-4 font-serif text-[21px]">{step.title}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-stone-600">{step.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Audience */}
         <section className="border-t border-stone-200 bg-secondary px-5 py-24 sm:px-6 md:py-32">
           <div className="mx-auto max-w-6xl">
              <p className="text-[12px] font-medium uppercase tracking-[0.08em] text-stone-600">{t.audience.eyebrow}</p>
             <h2 className="mt-4 text-[38px] leading-[1.04] sm:text-[52px]">{t.audience.heading}</h2>
             <div className="mt-10 grid gap-px overflow-hidden rounded-lg border border-stone-200 bg-stone-200 md:grid-cols-2">
              {[
                 { id: "couples", data: t.audience.couples, tone: "bg-rust-50" },
                 { id: "planners", data: t.audience.planners, tone: "bg-card" },
              ].map(({ id, data, tone }) => (
                <article
                  key={id}
                  id={id}
                  className={`scroll-mt-20 p-7 sm:p-9 ${tone}`}
                >
                  <h3 className="font-serif text-[24px]">{data.title}</h3>
                  <p className="mt-1 text-[15px] font-medium text-rust-600">{data.tagline}</p>
                  <p className="mt-3 text-[15px] leading-relaxed text-stone-700">{data.body}</p>
                  <ul className="mt-5 space-y-2.5">
                    {data.points.map((point) => (
                      <li key={point} className="flex items-start gap-2.5 text-[15px] text-stone-700">
                        <Check className="mt-1 h-4 w-4 shrink-0 text-sage-600" aria-hidden="true" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Trust */}
         <section className="border-y border-stone-200 px-5 py-20 sm:px-6">
           <div className="mx-auto max-w-5xl">
             <h2 className="text-center font-serif text-[30px] sm:text-[40px]">{t.trust.heading}</h2>
            <ul className="mt-8 grid gap-4 sm:grid-cols-2">
              {t.trust.points.map((point) => (
                <li key={point} className="flex items-start gap-2.5 text-[15px] leading-relaxed text-stone-600">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-rust-500" aria-hidden="true" />
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Final CTA */}
         <section ref={finalRef} className="px-5 py-24 sm:px-6 md:py-32">
           <div className="mx-auto max-w-6xl">
             <div className="grid items-center gap-10 lg:grid-cols-[1fr_minmax(380px,520px)] lg:gap-20">
              <div>
                  <p className="text-[12px] font-medium uppercase tracking-[0.08em] text-stone-600">
                  {t.finalCta.eyebrow}
                </p>
                 <h2 className="mt-4 max-w-[16ch] text-[38px] leading-[1.04] sm:text-[54px]">
                  {t.finalCta.heading}
                </h2>
                <p className="mt-4 max-w-[46ch] text-[17px] leading-relaxed text-stone-600">{t.finalCta.subtitle}</p>
              </div>
                <div className="border border-stone-200 bg-card p-5 shadow-soft sm:p-7">
                 <WaitlistForm idPrefix="final" formId="final" t={t} shareUrl={canonicalUrl} onSubmit={handleSubmit} />
             </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-stone-200 px-5 py-10 sm:px-6">
        <div className="mx-auto flex max-w-6xl flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div>
            <Logo size={28} />
            <p className="mt-3 max-w-[38ch] text-[14px] text-stone-600">{t.footer.tagline}</p>
             <p className="mt-2 max-w-[46ch] text-[13px] text-stone-600">{currentLocale === "fr" ? "Nous utilisons votre e-mail uniquement pour les nouvelles sur l’accès anticipé." : "We only use your email for early-access updates."}</p>
          </div>
          <div className="flex flex-col gap-4 md:items-end">
            <div className="flex flex-wrap items-center gap-4 text-[14px] md:justify-end">
              <a
                href={currentLocale === "fr" ? "/fr/application-mariage" : "/en/wedding-planning-app"}
                className="text-rust-600 transition-colors duration-200 hover:underline"
              >
                {t.nav.product}
              </a>
              <a
                href={currentLocale === "fr" ? "/fr/pour-organisateurs-de-mariage" : "/en/for-wedding-planners"}
                className="text-rust-600 transition-colors duration-200 hover:underline"
              >
                {t.nav.planners}
              </a>
              <a
                href={currentLocale === "fr" ? "/fr/organiser-un-mariage-par-pays" : "/en/wedding-planning-by-country"}
                className="text-rust-600 transition-colors duration-200 hover:underline"
              >
                {currentLocale === "fr" ? "Par pays" : "By country"}
              </a>
              <a
                href={currentLocale === "fr" ? "/fr/alternative-application-mariage" : "/en/wedding-planning-app-alternative"}
                className="text-rust-600 transition-colors duration-200 hover:underline"
              >
                {currentLocale === "fr" ? "Bien choisir" : "Choosing a tool"}
              </a>
              <a
                href={currentLocale === "fr" ? "/fr/checklist-mariage" : "/en/wedding-planning-checklist"}
                className="text-rust-600 transition-colors duration-200 hover:underline"
              >
                {t.nav.checklist}
              </a>
              <a href={`/${currentLocale}/guides`} className="text-rust-600 transition-colors duration-200 hover:underline">
                {t.nav.guides}
              </a>
            </div>
             <div className="flex items-center gap-4 text-[14px]">
              <a
                href="https://www.facebook.com/profile.php?id=61583920627069"
                target="_blank"
                rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 text-rust-600 transition-colors duration-200 hover:underline"
              >
                <Facebook className="h-4 w-4" aria-hidden="true" />
                 Facebook
              </a>
              <a
                href="https://www.instagram.com/nellove.app/"
                target="_blank"
                rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 text-rust-600 transition-colors duration-200 hover:underline"
              >
                <Instagram className="h-4 w-4" aria-hidden="true" />
                 Instagram
              </a>
            </div>
            <div className="flex items-center gap-4 text-[13px] text-stone-600">
               <Button type="button" variant="link" onClick={() => switchLocale(otherLocale)} className="h-auto p-0 text-[13px] font-normal text-stone-600 hover:text-rust-600">
                {t.langSwitch[otherLocale]}
               </Button>
               <span>© 2026 Nellove</span>
               <span>{t.footer.location}</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Sticky mobile CTA */}
      <div
        className={`fixed inset-x-0 bottom-0 z-40 border-t border-stone-200 bg-background px-4 py-3 transition-transform duration-200 md:hidden ${
          showSticky ? "translate-y-0" : "translate-y-full"
        }`}
        aria-hidden={!showSticky}
      >
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13px] font-medium text-stone-700">{t.sticky.label}</span>
           <Button asChild tabIndex={showSticky ? 0 : -1} className="h-11 shrink-0 rounded-md px-4 text-sm font-semibold">
             <a href="#join">{t.sticky.cta}</a>
           </Button>
        </div>
      </div>
    </div>
  );
};

export default Index;
